import type { FieldErrors } from 'react-hook-form';

/**
 * Whether the user has requested reduced motion. Duplicated (3-line) from
 * OnboardingTimeline rather than refactored into a shared helper — extracting
 * it is not worth the churn inside a P1 fix (F091 Notes).
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Scroll the first invalid field into view and focus it.
 *
 * RHF's `form.trigger()` validates and returns `false` but — unlike
 * `form.handleSubmit()` — never honours `shouldFocusError`. Wizard step advance
 * is a plain onClick (by design, F069 #10), so on a failed step the error
 * message can render far outside the mobile viewport with no feedback. This
 * helper restores the "land on the problem" behaviour.
 *
 * Resolution order for the target element of the first error in `fieldOrder`:
 *   1. `[name="<field>"]` or `[data-error-anchor="<field>"]` (custom groups)
 *   2. the first visible `[role="alert"]` in the document (last-resort fallback)
 *
 * @param errors RHF `formState.errors`.
 * @param fieldOrder Field names in the step's visual order.
 */
export function focusFirstError(errors: FieldErrors, fieldOrder: string[]): void {
  if (typeof document === 'undefined') return;

  const firstInvalid = fieldOrder.find((name) => Boolean(errors[name]));
  if (!firstInvalid) return;

  let el = document.querySelector<HTMLElement>(
    `[name="${firstInvalid}"], [data-error-anchor="${firstInvalid}"]`
  );

  // Custom fields (e.g. checkbox groups) may not expose a focusable anchor —
  // fall back to the first rendered error message.
  if (!el) {
    el = document.querySelector<HTMLElement>('[role="alert"]');
  }
  if (!el) return;

  el.scrollIntoView({
    block: 'center',
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
  // preventScroll avoids a second "jump" after scrollIntoView (F031 trick).
  el.focus({ preventScroll: true });
}
